import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ClipboardList, Package, Pencil, Plus, Trash2, UserCheck, Undo2 } from "lucide-react";

import { eventService, type EventFeedback, type EventResource, type EventTask, type SchoolEvent } from "../../services/eventService";
import { exportAsCsv, exportAsExcel, exportAsPdf } from "../../utils/exportTable";
import { ExportButtons, type ExportFormat } from "../ExportButtons";
import { errorLine } from "../inventory/InventoryParts";
import { StatusDot } from "../register/ClientRegister";
import { humanise } from "../register/registerLogic";
import { ResourceForm, TaskForm } from "./EventForms";
import { RESOURCE_STATUSES, RESOURCE_TYPES, day, guestOrder, isOpenTask, isWaiting, labelFrom, money, ratingSummary, registrationStatus, resourceCost, stars, taskStatus } from "./eventsLogic";

const Heading = ({ children, aside }: { children: React.ReactNode; aside?: React.ReactNode }) => (
  <div className="flex flex-wrap items-center justify-between gap-2">
    <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">{children}</h4>
    {aside}
  </div>
);

/** Who registered, checked in at the door on the day; printable for the door. */
export function GuestList({ event, today, onChanged }: { event: SchoolEvent; today: string; onChanged: () => void }) {
  const queryClient = useQueryClient();
  const key = ["events", "guests", event.id];
  const guests = useQuery({ queryKey: key, queryFn: () => eventService.getEventRegistrations(event.id) });
  const [search, setSearch] = useState("");
  const done = () => {
    queryClient.invalidateQueries({ queryKey: key });
    onChanged();
  };
  const checkIn = useMutation({ mutationFn: eventService.checkIn, onSuccess: done });
  const undo = useMutation({ mutationFn: eventService.undoCheckIn, onSuccess: done });
  const rows = guestOrder(guests.data ?? []);
  const shown = rows.filter((row) => !search.trim() || `${row.user_name} ${row.user_type}`.toLowerCase().includes(search.trim().toLowerCase()));
  const doorOpen = ["published", "completed"].includes(event.status) && event.start_date <= today;
  const waiting = rows.filter(isWaiting).length;
  const came = rows.filter((row) => row.status === "attended").length;

  const exportGuests = (format: ExportFormat) => {
    const columns = [
      { header: "Name", accessor: (row: (typeof rows)[number]) => row.user_name },
      { header: "Who", accessor: (row: (typeof rows)[number]) => humanise(row.user_type) },
      { header: "Status", accessor: (row: (typeof rows)[number]) => registrationStatus(row).label },
      { header: "Food needs", accessor: (row: (typeof rows)[number]) => row.dietary_requirements ?? "" },
      { header: "Requests", accessor: (row: (typeof rows)[number]) => row.special_requests ?? "" },
      { header: "Emergency contact", accessor: (row: (typeof rows)[number]) => [row.emergency_contact, row.emergency_phone].filter(Boolean).join(", ") },
    ];
    const live = shown.filter((row) => row.status !== "cancelled");
    const title = `Guest list: ${event.name} (${day(event.start_date)})`;
    if (format === "csv") exportAsCsv(`guest-list-${event.id}.csv`, columns, live);
    if (format === "excel") exportAsExcel(`guest-list-${event.id}.xls`, title, columns, live);
    if (format === "pdf") exportAsPdf(title, columns, live);
  };

  return (
    <section className="space-y-2">
      <Heading aside={<ExportButtons onExport={exportGuests} disabled={!rows.some((row) => row.status !== "cancelled")} what="the guest list" />}>
        Guest list ({event.registered_count}{event.max_attendees ? ` of ${event.max_attendees} places` : ""})
      </Heading>
      <p className="text-sm text-gray-600">
        {came} came{waiting ? `, ${waiting} still expected` : ""}.{" "}
        {!doorOpen && event.status !== "cancelled" && "Check-in opens on the day the event starts."}
      </p>
      {rows.length > 8 && <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Find a name" aria-label="Find a guest" className="input h-9 max-w-xs" />}
      {guests.isLoading ? (
        <p className="text-sm text-gray-500">Loading the guest list…</p>
      ) : rows.length ? (
        <div className="sheet-wrap max-h-80 overflow-y-auto">
          <table className="sheet">
            <thead>
              <tr>
                <th className="sheet-rownum">#</th>
                <th>Name</th>
                <th>Who</th>
                <th>Needs</th>
                <th>Status</th>
                <th className="text-right">Door</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((row, index) => (
                <tr key={row.id}>
                  <td className="sheet-rownum">{index + 1}</td>
                  <td className="font-medium text-gray-900">{row.user_name}</td>
                  <td className="text-gray-700">{humanise(row.user_type)}</td>
                  <td className="max-w-[14rem] whitespace-normal text-gray-700">{[row.dietary_requirements, row.special_requests].filter(Boolean).join("; ") || "—"}</td>
                  <td>
                    <StatusDot {...registrationStatus(row)} />
                    {row.check_in_time && <span className="ml-1 text-xs tabular-nums text-gray-500">{new Date(row.check_in_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>}
                  </td>
                  <td className="text-right">
                    {doorOpen && (isWaiting(row) || row.status === "no_show") && (
                      <button type="button" disabled={checkIn.isPending} onClick={() => checkIn.mutate(row.id)} className="btn btn-primary h-7 min-h-7 gap-1 px-2 text-xs">
                        <UserCheck className="h-3.5 w-3.5" /> Check in
                      </button>
                    )}
                    {row.status === "attended" && (
                      <button type="button" disabled={undo.isPending} onClick={() => undo.mutate(row.id)} className="btn btn-outline h-7 min-h-7 gap-1 px-2 text-xs" title="Checked in by mistake">
                        <Undo2 className="h-3.5 w-3.5" /> Undo
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-600">{event.status === "published" ? "Nobody has registered yet." : "Registration opens when the event is published."}</p>
      )}
      {[checkIn, undo].map((mutation, index) => mutation.isError && <div key={index}>{errorLine(mutation.error, "That could not be recorded.")}</div>)}
    </section>
  );
}

/** The tasks and the things an event needs, while it is being planned. */
export function Planning({ event, events, tasks, today, onChanged }: { event: SchoolEvent; events: SchoolEvent[]; tasks: EventTask[]; today: string; onChanged: () => void }) {
  const queryClient = useQueryClient();
  const key = ["events", "resources", event.id];
  const resources = useQuery({ queryKey: key, queryFn: () => eventService.getEventResources(event.id) });
  const [taskForm, setTaskForm] = useState<EventTask | "new" | null>(null);
  const [resourceForm, setResourceForm] = useState<EventResource | "new" | null>(null);
  const refreshResources = () => queryClient.invalidateQueries({ queryKey: key });
  const removeResource = useMutation({ mutationFn: eventService.deleteResource, onSuccess: refreshResources });
  const finished = event.status === "cancelled" || event.status === "completed";
  const mine = tasks.filter((row) => row.event === event.id).sort((a, b) => Number(isOpenTask(b)) - Number(isOpenTask(a)) || (a.due_date ?? "9999").localeCompare(b.due_date ?? "9999"));
  const items = resources.data ?? [];
  const cost = resourceCost(items);

  return (
    <>
      <section className="space-y-2">
        <Heading aside={!finished && <button type="button" onClick={() => setTaskForm("new")} className="btn btn-outline h-8 min-h-8 gap-1 px-2.5 text-xs"><Plus className="h-3.5 w-3.5" /> Add a task</button>}>
          Tasks ({mine.filter(isOpenTask).length} open)
        </Heading>
        {mine.length ? (
          <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
            {mine.map((row) => (
              <li key={row.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                <span className="min-w-0">
                  <span className={`font-medium ${row.status === "cancelled" ? "text-gray-400 line-through" : "text-gray-900"}`}>{row.title}</span>
                  <span className="block text-xs text-gray-500">
                    {row.assigned_to_name}
                    {row.due_date ? ` · due ${day(row.due_date)}` : ""}
                    {row.priority === "urgent" || row.priority === "high" ? ` · ${humanise(row.priority)} priority` : ""}
                  </span>
                </span>
                <span className="flex items-center gap-2">
                  <StatusDot {...taskStatus(row, today)} />
                  <button type="button" onClick={() => setTaskForm(row)} className="rounded p-1.5 text-gray-500 hover:bg-gray-100" aria-label={`Change ${row.title}`}>
                    <Pencil className="h-4 w-4" />
                  </button>
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="flex items-center gap-2 rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-600"><ClipboardList className="h-4 w-4" /> No tasks. Add who is doing what, e.g. booking the hall or printing programmes.</p>
        )}
      </section>

      <section className="space-y-2">
        <Heading aside={!finished && <button type="button" onClick={() => setResourceForm("new")} className="btn btn-outline h-8 min-h-8 gap-1 px-2.5 text-xs"><Plus className="h-3.5 w-3.5" /> Add an item</button>}>
          What it needs{cost ? ` · about ${money(cost)}` : ""}
        </Heading>
        {items.length ? (
          <div className="sheet-wrap">
            <table className="sheet">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Kind</th>
                  <th className="text-right">How many</th>
                  <th className="text-right">Cost</th>
                  <th>Supplier</th>
                  <th>Status</th>
                  <th className="text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.id}>
                    <td className="font-medium text-gray-900">{row.name}</td>
                    <td className="text-gray-700">{labelFrom(RESOURCE_TYPES, row.resource_type)}</td>
                    <td className="text-right tabular-nums">{row.quantity} {row.unit}</td>
                    <td className="text-right tabular-nums">{row.cost ? money(Number(row.cost)) : "—"}</td>
                    <td className="text-gray-700">{row.supplier || "—"}</td>
                    <td className="text-gray-700">{labelFrom(RESOURCE_STATUSES, row.status)}</td>
                    <td className="whitespace-nowrap text-right">
                      <button type="button" onClick={() => setResourceForm(row)} className="rounded p-1 text-gray-500 hover:bg-gray-100" aria-label={`Change ${row.name}`}><Pencil className="h-4 w-4" /></button>
                      <button type="button" disabled={removeResource.isPending} onClick={() => removeResource.mutate(row.id)} className="rounded p-1 text-red-600 hover:bg-red-50" aria-label={`Remove ${row.name}`}><Trash2 className="h-4 w-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="flex items-center gap-2 rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-600"><Package className="h-4 w-4" /> Nothing listed. Add the venue, equipment, catering or transport it needs.</p>
        )}
        {removeResource.isError && errorLine(removeResource.error, "It could not be removed.")}
      </section>

      {taskForm && (
        <TaskForm
          task={taskForm === "new" ? undefined : taskForm}
          events={events}
          eventId={event.id}
          onClose={() => setTaskForm(null)}
          onDone={() => {
            setTaskForm(null);
            onChanged();
          }}
        />
      )}
      {resourceForm && (
        <ResourceForm
          resource={resourceForm === "new" ? undefined : resourceForm}
          eventId={event.id}
          onClose={() => setResourceForm(null)}
          onDone={() => {
            setResourceForm(null);
            refreshResources();
          }}
        />
      )}
    </>
  );
}

/** How people rated an event, at a glance. */
export function FeedbackSummary({ feedback }: { feedback: EventFeedback[] }) {
  const summary = ratingSummary(feedback);
  if (!summary.count) return null;
  const widest = Math.max(...summary.counts.map((row) => row.count), 1);
  return (
    <section className="space-y-2">
      <Heading>What people thought ({summary.count})</Heading>
      <div className="grid gap-4 rounded-lg border border-gray-200 p-4 sm:grid-cols-[auto_1fr]">
        <div>
          <p className="text-3xl font-semibold tabular-nums text-gray-900">{summary.average!.toFixed(1)}</p>
          <p className="text-sm text-amber-600" aria-hidden>{stars(Math.round(summary.average!))}</p>
          <p className="mt-1 text-xs text-gray-500">{summary.recommend}% would recommend it · {summary.again}% would come again</p>
        </div>
        <ul className="space-y-1">
          {summary.counts.map((row) => (
            <li key={row.rating} className="flex items-center gap-2 text-xs text-gray-600">
              <span className="w-10 tabular-nums">{row.rating} ★</span>
              <span className="h-2 flex-1 rounded bg-gray-100">
                <span className="block h-2 rounded bg-amber-400" style={{ width: `${(row.count / widest) * 100}%` }} />
              </span>
              <span className="w-6 text-right tabular-nums">{row.count}</span>
            </li>
          ))}
        </ul>
      </div>
      {feedback.filter((row) => row.comments?.trim()).slice(0, 3).map((row) => (
        <blockquote key={row.id} className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-700">
          “{row.comments}” <span className="text-xs text-gray-500">({stars(row.overall_rating)})</span>
        </blockquote>
      ))}
    </section>
  );
}
